import React, { useEffect, useState, useCallback, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import AnnouncementIcon from "@mui/icons-material/Announcement";
import { useDispatch, useSelector } from "react-redux";
import { COMMON_SERVICES } from "redux/reduxToolkitServices/_commonServices/commonServices";
import { useTranslation } from "react-i18next";
import { useHookstate } from "@hookstate/core";
import { SITEID_GLOBAL_STATE } from "globalStore/globalStore";
import {
    StyledDialogTitle,
    StyledDialogContent,
    StyledDialogActions,
    AnnouncementContent
} from "./AnnouncementPopupStyled";
import { useRedirectToPage } from "commonComponents/Navigation";

const Interval = 300000; 
const DismissedIds_key = "dismissedIds";
const SeverityColors = {
    critical: "#ff0000",
    warning: "#ef8a24",
    info: "#e0b445",
    default: "var(--theme-text)"
};

const SeverityIcons = {
    critical: ErrorIcon,
    warning: WarningIcon,
    info: InfoIcon,
    default: AnnouncementIcon
};

const getDismissedIds = () => {
        return JSON.parse(localStorage.getItem(DismissedIds_key) || "[]");
    
};

const saveDismissedId = (announcementId) => {
        const dismissedIds = getDismissedIds();
        if (!dismissedIds.includes(announcementId)) {
            dismissedIds.push(announcementId);
            localStorage.setItem(DismissedIds_key, JSON.stringify(dismissedIds));
        }
};

const getAnnouncementsDifference = (array1, array2) => {
    return array1.filter(item1 => !array2.some(item2 => item1.Id === item2.Id));
};

const getSeverityColor = (severity) => {
    return SeverityColors[severity?.toLowerCase()] || SeverityColors.default;
};

const getSeverityIcon = (severity) => {
    return SeverityIcons[severity?.toLowerCase()] || SeverityIcons.default;
};
const AnnouncementPopup = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const [currentAnnouncement, setCurrentAnnouncement] = useState(null);
    const [announcementQueue, setAnnouncementQueue] = useState([]);
    const [oldAnnouncementsBackup, setOldAnnouncementsBackup] = useState([]);
    const previousAckStatusRef = useRef("");
    const { siteId } = useHookstate(SITEID_GLOBAL_STATE);
    const currentSiteId = siteId.get();
    const { getAnnouncementsData } = useSelector((state) => state.commonReducers) || {};
    const acknowledgeAnnouncementsStatus = useSelector(
        (state) => state.commonReducers.acknowledgeAnnouncementsStatus
    );
    const getUserRole = useSelector((state) => state.commonReducers.getUserRole);
    const username = getUserRole?.Name || "";
    const redirectToPage = useRedirectToPage();

    const handleClose = useCallback(() => {
        setOpen(false);
        setCurrentAnnouncement(null);
        setAnnouncementQueue([]);
    }, []);

    const handleNext = useCallback(() => {
        setAnnouncementQueue(prevQueue => {
            const remainingQueue = prevQueue.slice(1);
            if (remainingQueue.length > 0) {
                setCurrentAnnouncement(remainingQueue[0]);
                return remainingQueue;
            }
            handleClose();
            return [];
        });
    }, [handleClose]);

    const handleDismiss = useCallback(() => {
        if (currentAnnouncement) {
            saveDismissedId(currentAnnouncement.Id);
            handleNext();
        }
    }, [currentAnnouncement, handleNext]);

    const handleViewAnnouncements = useCallback(() => {
        setOpen(false);
        redirectToPage("announcements");
    }, []);

    const handleAcknowledge = useCallback(() => {
        if (currentAnnouncement && username && currentSiteId) {
            dispatch(
                COMMON_SERVICES.acknowledgeAnnouncementsServices(
                    currentSiteId,
                    [currentAnnouncement.Id],
                    username
                )
            );
            saveDismissedId(currentAnnouncement.Id);
            handleNext();
        }
    }, [currentAnnouncement, username, currentSiteId, dispatch, handleNext]);

    useEffect(() => {
        if (!currentSiteId) return;

        dispatch(COMMON_SERVICES.getAnnouncementsServices(currentSiteId));

        const interval = setInterval(() => {
            dispatch(COMMON_SERVICES.getAnnouncementsServices(currentSiteId));
        }, Interval);

        return () => clearInterval(interval);
    }, [currentSiteId, dispatch]);

    useEffect(() => {
        if (!getAnnouncementsData?.Messages || !Array.isArray(getAnnouncementsData.Messages)) {
            return;
        }

        const currentMessages = getAnnouncementsData.Messages;
        const oldMessages = oldAnnouncementsBackup?.Messages || [];
        const dismissedIds = getDismissedIds();

        let newAnnouncements = getAnnouncementsDifference(currentMessages, oldMessages);
        newAnnouncements = newAnnouncements.filter(item => !item.IsAcknowledged && !dismissedIds.includes(item.Id));
        if (newAnnouncements.length > 0 && !open) {
            setAnnouncementQueue(newAnnouncements);
            setCurrentAnnouncement(newAnnouncements[0]);
            setOpen(true);
        }
        setOldAnnouncementsBackup(getAnnouncementsData);
    }, [getAnnouncementsData, open]); 
    useEffect(() => {
        if (acknowledgeAnnouncementsStatus === "SUCCESS" && 
            previousAckStatusRef.current !== "SUCCESS") {
            handleNext();
        }
        previousAckStatusRef.current = acknowledgeAnnouncementsStatus;
    }, [acknowledgeAnnouncementsStatus, handleNext]);

    if (!currentAnnouncement) return null;

    const SeverityIconComponent = getSeverityIcon(currentAnnouncement.Severity);
    const severityColor = getSeverityColor(currentAnnouncement.Severity);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <StyledDialogTitle severityColor={severityColor}>
                <div className="title-content">
                    <SeverityIconComponent className="star-icon" />
                    <span>{currentAnnouncement.Title || t("Announcement")}</span>
                </div>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    className="close-button"
                >
                    <CloseIcon />
                </IconButton>
            </StyledDialogTitle>
            
            <StyledDialogContent dividers>
                <AnnouncementContent>
                    <div className="announcement-text">
                        {currentAnnouncement.Text}
                    </div>
                </AnnouncementContent>
            </StyledDialogContent>
            
            <StyledDialogActions>
                <div className="action-wrapper">
                    {announcementQueue.length > 1 && (
                        <div className="queue-info">
                            {t("Announcement")} 1 {t("of")} {announcementQueue.length}
                        </div>
                    )}
                    <button className="send-btn" onClick={handleViewAnnouncements}>
                        {t("View_All")}
                    </button>
                    <button className="send-btn" onClick={handleDismiss}>
                        {t("Dismiss")}
                    </button>
                    <Button 
                        variant="outlined" 
                        className="active"
                        onClick={handleAcknowledge}
                    >
                        {t("Acknowledge")}
                    </Button>
                </div>
            </StyledDialogActions>
        </Dialog>
    );
};

export default AnnouncementPopup;